import { useCallback, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { downloadDocument, fmtErr } from "@/lib/api";
import { useProject } from "@/context/ProjectContext";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { StatusBadge, fmtDate, fmtMoney } from "@/components/badges";
import { Download, Pencil, Plus, Send, ShieldAlert, Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { PendingActionsCard } from "@/components/payments/PendingActionsCard";
import { CreatePaymentDialog } from "@/components/payments/CreatePaymentDialog";
import { EditPaymentDialog } from "@/components/payments/EditPaymentDialog";
import { useConfirm } from "@/components/ConfirmDialog";

export default function Payments() {
  const { askConfirm, confirmDialog } = useConfirm();
  const { selectedId } = useProject();
  const { user } = useAuth();
  const { t } = useTranslation();
  const isAdmin = !!(user?.is_super_admin || user?.is_admin);
  const canAct = isAdmin || !!user?.can_view_payments;
  const qc = useQueryClient();
  const [displayCur, setDisplayCur] = useState("USD");
  const [createOpen, setCreateOpen] = useState(false);
  const [editRec, setEditRec] = useState(null);

  const { data: payments, isLoading, error } = useQuery({
    queryKey: ["payments", selectedId],
    queryFn: async () => (await api.get(`/projects/${selectedId}/payments`)).data,
    enabled: !!selectedId,
    retry: false,
  });

  const restricted = error?.response?.status === 403;

  const { data: fx } = useQuery({
    queryKey: ["fx-rates"],
    queryFn: async () => (await api.get("/fx-rates")).data,
    staleTime: 30 * 60 * 1000,
  });
  const convert = useCallback((amount, from) => {
    const rates = fx?.aed_per || {};
    if (!from || from === displayCur) return amount;
    const aed = amount * (rates[from] || 1);
    return aed / (rates[displayCur] || 1);
  }, [fx, displayCur]);

  const { data: parties = [] } = useQuery({
    queryKey: ["parties", selectedId],
    queryFn: async () => (await api.get(`/projects/${selectedId}/parties`)).data,
    enabled: !!selectedId && !restricted,
  });
  const clientName =
    (parties.find((p) => (p.role || "").trim().toLowerCase() === "client") || parties.find((p) => (p.role || "").trim().toLowerCase().startsWith("client")))?.company_name || "the Client";

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["payments", selectedId] });
    qc.invalidateQueries({ queryKey: ["dashboard", selectedId] });
  };

  const updateMut = useMutation({
    mutationFn: async ({ id, body }) => api.put(`/payments/${id}`, body),
    onSuccess: (r) => {
      if (r?.data?.pending) {
        toast.success(t("payments.toastPending"));
        qc.invalidateQueries({ queryKey: ["payment-actions", selectedId] });
      } else {
        toast.success(t("payments.toastUpdated"));
      }
      invalidate();
    },
    onError: (e) => toast.error(fmtErr(e)),
  });

  const delMut = useMutation({
    mutationFn: async (id) => api.delete(`/payments/${id}`),
    onSuccess: (r) => {
      if (r?.data?.pending) {
        toast.success(t("payments.toastPending"));
        qc.invalidateQueries({ queryKey: ["payment-actions", selectedId] });
      } else {
        toast.success(t("payments.toastDeleted"));
      }
      invalidate();
    },
    onError: (e) => toast.error(fmtErr(e)),
  });

  const summary = useMemo(() => {
    const rows = payments || [];
    const sum = (f) => rows.filter(f).reduce((s, p) => s + convert(p.amount, p.currency), 0);
    return {
      ipc: sum((p) => p.kind === "ipc"),
      payments: sum((p) => p.kind === "payment"),
      awaiting: sum((p) => p.status === "sent" || p.status === "approved"),
      paid: sum((p) => p.status === "paid"),
    };
  }, [payments, convert]);

  if (restricted) {
    return (
      <Card className="border-amber-200" data-testid="payments-restricted">
        <CardContent className="flex flex-col items-center gap-3 py-16 text-center">
          <ShieldAlert className="h-10 w-10 text-amber-600" />
          <h2 className="font-display text-xl font-bold">{t("payments.restrictedTitle")}</h2>
          <p className="max-w-md text-sm text-slate-600">
            {t("payments.restrictedBody")}
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6" data-testid="payments-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight">{t("payments.title")}</h1>
          <p className="text-sm text-slate-500">
            {isAdmin
              ? t("payments.subtitleAdmin", { client: clientName })
              : t("payments.subtitleClient", { client: clientName })}
          </p>
          {fx?.fetched_at && (
            <p className="mt-1 text-xs text-slate-400" data-testid="payments-fx-source">{t("payments.fxSource", { source: fx.source, date: fmtDate(fx.fetched_at) })}</p>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-1.5">
            <Label className="text-xs text-slate-500">{t("payments.displayCurrency")}</Label>
            <Select value={displayCur} onValueChange={setDisplayCur}>
              <SelectTrigger className="w-24" data-testid="payments-display-currency"><SelectValue /></SelectTrigger>
              <SelectContent>
                {["USD", "EUR", "AED"].map((c) => (
                  <SelectItem key={c} value={c} data-testid={`display-currency-${c}`}>{c}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {canAct && (
            <Button onClick={() => setCreateOpen(true)} className="bg-slate-900 hover:bg-slate-800" data-testid="new-payment-button">
              <Plus className="mr-2 h-4 w-4" /> {t("payments.newRecord")}
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Card data-testid="ipc-summary-ipc"><CardContent className="p-4"><div className="font-mono text-xs uppercase tracking-wider text-slate-500">{t("dashboard.ipcClaimed")}</div><div className="mt-1 font-mono text-lg font-bold">{fmtMoney(summary.ipc, displayCur)}</div></CardContent></Card>
        <Card data-testid="ipc-summary-payments"><CardContent className="p-4"><div className="font-mono text-xs uppercase tracking-wider text-slate-500">{t("dashboard.paymentsMade")}</div><div className="mt-1 font-mono text-lg font-bold">{fmtMoney(summary.payments, displayCur)}</div></CardContent></Card>
        <Card data-testid="ipc-summary-awaiting"><CardContent className="p-4"><div className="font-mono text-xs uppercase tracking-wider text-slate-500">{t("payments.awaiting")}</div><div className="mt-1 font-mono text-lg font-bold text-amber-600">{fmtMoney(summary.awaiting, displayCur)}</div></CardContent></Card>
        <Card data-testid="ipc-summary-paid"><CardContent className="p-4"><div className="font-mono text-xs uppercase tracking-wider text-slate-500">{t("dashboard.paid")}</div><div className="mt-1 font-mono text-lg font-bold text-emerald-600">{fmtMoney(summary.paid, displayCur)}</div></CardContent></Card>
      </div>

      <PendingActionsCard canAct={canAct} isAdmin={isAdmin} />

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">{t("payments.records")}</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("payments.thDate")}</TableHead>
                <TableHead>{t("payments.thType")}</TableHead>
                <TableHead>{t("common.description")}</TableHead>
                <TableHead className="text-right">{t("payments.thAmount")}</TableHead>
                <TableHead>{t("common.status")}</TableHead>
                <TableHead>{t("payments.thDocument")}</TableHead>
                {canAct && <TableHead className="text-right">{t("common.actions")}</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading && <TableRow><TableCell colSpan={7} className="text-center text-sm text-slate-500">{t("common.loading")}</TableCell></TableRow>}
              {!isLoading && (payments || []).length === 0 && (
                <TableRow><TableCell colSpan={7} className="text-center text-sm text-slate-500" data-testid="payments-empty">{t("payments.empty")}</TableCell></TableRow>
              )}
              {(payments || []).map((p) => (
                <TableRow key={p.id} data-testid={`ipc-payment-row-${p.id}`}>
                  <TableCell className="font-mono text-xs">{fmtDate(p.date)}</TableCell>
                  <TableCell><StatusBadge status={p.kind} label={p.kind === "ipc" ? t("badges.ipc") : t("badges.payment")} data-testid={`ipc-payment-kind-${p.id}`} /></TableCell>
                  <TableCell className="text-sm">{p.description}</TableCell>
                  <TableCell className="text-right font-mono text-sm font-semibold" data-testid={`ipc-payment-amount-${p.id}`}>
                    {fmtMoney(convert(p.amount, p.currency), displayCur)}
                    {p.currency && p.currency !== displayCur && (
                      <div className="text-[10px] font-normal text-slate-400">{t("payments.original", { amount: fmtMoney(p.amount, p.currency) })}</div>
                    )}
                  </TableCell>
                  <TableCell><StatusBadge status={p.status} data-testid={`ipc-payment-status-${p.id}`} /></TableCell>
                  <TableCell>
                    {p.document_id ? (
                      <Button variant="ghost" size="icon" onClick={() => downloadDocument(p.document_id, p.document_name)} data-testid={`ipc-payment-download-${p.id}`}>
                        <Download className="h-4 w-4" />
                      </Button>
                    ) : (
                      <span className="text-xs text-slate-400">—</span>
                    )}
                  </TableCell>
                  {canAct && (
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        {p.status === "draft" && (
                          <Button size="sm" variant="outline" onClick={() => updateMut.mutate({ id: p.id, body: { status: "sent" } })} data-testid={`ipc-payment-send-${p.id}`}>
                            <Send className="mr-1 h-3.5 w-3.5" /> {t("payments.send")}
                          </Button>
                        )}
                        {(p.status === "sent" || p.status === "approved") && (
                          <Button size="sm" variant="outline" className="text-emerald-700" onClick={() => updateMut.mutate({ id: p.id, body: { status: "paid" } })} data-testid={`ipc-payment-pay-${p.id}`}>
                            {t("payments.markPaid")}
                          </Button>
                        )}
                        <Button variant="ghost" size="icon" title={t("payments.editTitle")} onClick={() => setEditRec(p)} data-testid={`ipc-payment-edit-${p.id}`}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          title={t("common.delete")}
                          className="text-rose-600 hover:text-rose-700"
                          onClick={() => askConfirm(t("payments.deleteConfirm", { kind: p.kind === "ipc" ? "IPC" : t("badges.payment"), amount: fmtMoney(p.amount, p.currency) }), () => delMut.mutate(p.id))}
                          data-testid={`ipc-payment-delete-${p.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <CreatePaymentDialog open={createOpen} onOpenChange={setCreateOpen} clientName={clientName} />
      <EditPaymentDialog rec={editRec} onClose={() => setEditRec(null)} clientName={clientName} />
      {confirmDialog}
    </div>
  );
}
